import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DEV_AUTH_ENABLED, fetchDevPersonas } from "../lib/devAuth";
import { useDevSession } from "../lib/devSession";
import { DEMO_STICKY_BAR_LIFT, isDemoWorkflowPath } from "../lib/demoRoutes";

const VISIBILITY_KEY = "dev-guest-bar-visible";
/** Matches FeedStickyBar height so the collapsed Dev toggle sits just above it on /events */
const FEED_STICKY_BAR_LIFT = "calc(4.125rem + env(safe-area-inset-bottom, 0px))";

function readBarVisible() {
  try {
    return localStorage.getItem(VISIBILITY_KEY) !== "false";
  } catch {
    return true;
  }
}

function PersonaRow({ label, people, kind, account, switching, onSwitch }) {
  if (!people.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-ui text-[10px] uppercase tracking-wider text-ink/45 flex items-center gap-1 shrink-0">
        <FlaskConical className="w-3 h-3" /> {label}
      </span>
      {people.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => onSwitch(kind, p.id, p.name || p.label)}
          disabled={switching || account?.id === p.organizer_id}
          className={personaButtonClass(account?.id === p.organizer_id, kind)}
          data-testid={`dev-${kind}-${p.id}`}
          title={p.description || p.label || p.name}
        >
          {p.name || p.label}
        </button>
      ))}
    </div>
  );
}

function personaButtonClass(active, kind) {
  const base = kind === "host" ? "dev-host-pill" : "dev-user-pill";
  return `${base}${active ? " active" : ""}`;
}

export default function DevGuestBar() {
  const { pathname } = useLocation();
  const aboveFeedStickyBar = pathname === "/events";
  const aboveDemoStickyBar = isDemoWorkflowPath(pathname);
  /** Only the collapsed toggle lifts above sticky chrome; the open panel pins to the screen bottom. */
  const toggleStickyLift = aboveDemoStickyBar
    ? DEMO_STICKY_BAR_LIFT
    : aboveFeedStickyBar
      ? FEED_STICKY_BAR_LIFT
      : null;
  const [hosts, setHosts] = useState([]);
  const [guests, setGuests] = useState([]);
  const [visible, setVisible] = useState(readBarVisible);
  const { account } = useAuth();
  const { switching, switchPersona } = useDevSession();

  useEffect(() => {
    if (!DEV_AUTH_ENABLED) return;
    fetchDevPersonas()
      .then((data) => {
        setHosts(data.hosts || []);
        setGuests(data.guests || []);
      })
      .catch(() => {});
  }, []);

  const rows = useMemo(() => {
    const dancers = guests.filter((g) => (g.group || "dancer") === "dancer");
    const instructors = guests.filter((g) => g.group === "instructor");
    const studioOwners = hosts.filter((h) => h.group === "studio_owner");
    const otherHosts = hosts.filter((h) => (h.group || "host") !== "studio_owner");
    return [
      { label: "Dev user", kind: "guest", people: dancers },
      { label: "Dev instructor", kind: "guest", people: instructors },
      { label: "Dev studio owner", kind: "host", people: studioOwners },
      { label: "Dev host", kind: "host", people: otherHosts },
    ];
  }, [guests, hosts]);

  if (!DEV_AUTH_ENABLED || (hosts.length === 0 && guests.length === 0)) return null;

  async function handleSwitch(kind, personaId, label) {
    try {
      const data = await switchPersona(kind, personaId);
      const name = data?.organizer?.name || label;
      toast.success(name);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  function setBarVisible(next) {
    setVisible(next);
    try {
      localStorage.setItem(VISIBILITY_KEY, String(next));
    } catch {
      // ignore storage failures in dev tooling
    }
  }

  if (!visible) {
    const toggleStyle = toggleStickyLift
      ? { bottom: `calc(${toggleStickyLift} + 0.5rem)` }
      : undefined;
    return (
      <button
        type="button"
        onClick={() => setBarVisible(true)}
        className={`dev-bar-toggle fixed right-3 z-50 ${toggleStickyLift ? "" : "bottom-3"}`}
        style={toggleStyle}
        data-testid="dev-guest-bar-show"
        aria-label="Show dev user bar"
        title="Show dev user bar"
      >
        <FlaskConical className="w-3.5 h-3.5" />
        Dev
        <ChevronUp className="w-3.5 h-3.5 opacity-60" />
      </button>
    );
  }

  return (
    <div
      className="dev-bar fixed inset-x-0 bottom-0 z-50 px-3 py-2"
      data-testid="dev-guest-bar"
    >
      <div className="flex items-start gap-2">
        <div className="w-full min-w-0 space-y-1.5 flex-1">
          {rows.map((row) => (
            <PersonaRow
              key={row.label}
              label={row.label}
              people={row.people}
              kind={row.kind}
              account={account}
              switching={switching}
              onSwitch={handleSwitch}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => setBarVisible(false)}
          className="dev-bar-hide shrink-0"
          data-testid="dev-guest-bar-hide"
          aria-label="Hide dev user bar"
          title="Hide dev user bar"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
